/**
 * 49. Goods Receipts — receiving against PO; quantity checks; quality
 * disposition; putaway; over/under tolerances.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
  BlockedActionExplanation,
} from "@/components/ui";
import { useTranslation } from "@/lib/i18n";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { formatDate } from "@/lib/money";

interface GoodsReceiptRow {
  id: string;
  receiptNumber: string;
  purchaseOrderNumber: string;
  supplierName: string;
  warehouseName: string;
  receiptDate: string;
  lineCount: number;
  varianceCount: number;
  status: "Draft" | "Posted" | "Reversed";
}

export function GoodsReceiptsPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<GoodsReceiptRow>(
    ["procurement", "goods-receipts"],
    "/procurement/goods-receipts",
  );

  const withVariance = (data?.items ?? []).filter((r) => r.varianceCount > 0);

  return (
    <div>
      <PageHeader
        title="Goods Receipts"
        subtitle="Receive against purchase orders; over/under tolerance checks; quality disposition."
        actions={
          <PendingButton
            variant="primary"
            reason="purchaseEditor"
            slice="ED-W3"
          >
            {t("documentActions.newGoodsReceipt")}
          </PendingButton>
        }
      />
      {withVariance.length > 0 && (
        <BlockedActionExplanation
          title="Receipts outside tolerance"
          reasons={withVariance.map((r) => ({
            code: "RECEIPT_VARIANCE",
            message: `Receipt ${r.receiptNumber} has ${r.varianceCount} line(s) outside the configured tolerance.`,
            resolutionPath:
              "Open the receipt, review the variances, and either accept with reason or adjust quantity.",
          }))}
        />
      )}
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Goods receipts (${data.total})`}>
          <DataTable<GoodsReceiptRow>
            rows={data.items}
            columns={[
              {
                key: "receiptNumber",
                label: "Number",
                render: (r) => (
                  <span className="atlas-mono">{r.receiptNumber}</span>
                ),
              },
              {
                key: "purchaseOrderNumber",
                label: "PO",
                render: (r) => (
                  <span className="atlas-mono">{r.purchaseOrderNumber}</span>
                ),
              },
              { key: "supplierName", label: "Supplier" },
              { key: "warehouseName", label: "Warehouse" },
              {
                key: "receiptDate",
                label: "Date",
                render: (r) => formatDate(r.receiptDate),
              },
              { key: "lineCount", label: "Lines", numeric: true },
              {
                key: "varianceCount",
                label: "Variances",
                numeric: true,
                render: (r) =>
                  r.varianceCount > 0 ? (
                    <Badge tone="danger">{r.varianceCount}</Badge>
                  ) : (
                    "—"
                  ),
              },
              {
                key: "status",
                label: "Status",
                render: (r) => <StatusBadge status={r.status} />,
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
